/**
 * GLSL code intended to replace the three.js "normal_fragment_maps" shader chunk.
 * We add a "normal_entry_point" that can be hooked into to modify mapN, as well as including normal processing even
 * if there's no normal map.
 * @module
 */
import { SSFXRendererOutput } from "../../threejs/higharc/SSFXRendererOutput";

// Below we need to mix normals from up to three different sources: the normal map, the layer-based height, and the parallax map (dmDetailNormal).
// We use a fast approximation for mixing tangent-space normals together. Reference: https://blog.selfshadow.com/publications/blending-in-detail/
export function normalFragmentMapsChunk(normalMapOnly: boolean) {
  const applyHeightNormals = normalMapOnly
    ? ""
    : `
    #ifdef SUPPORT_HEIGHT_LAYERS
      vec4 dmHeightBasedNormalAndAO = dmNormalAndAOFromHeight(uvWithParallax);

      mapN = normalize(vec3(mapN.xy + dmHeightBasedNormalAndAO.xy, mapN.z * dmHeightBasedNormalAndAO.z));
    #endif
    #if defined(SSFX_MATERIAL_DEV) && SSFX_MATERIAL_DEV == 1
      if (ssfxMaterialOutputType == ${SSFXRendererOutput.LIGHT_WITHOUT_DETAIL.toFixed(0)}) {
        mapN = vec3(0.0, 0.0, 1.0);
      }
    #endif`;

  return `

#if defined(METAL_SCREEN_MATERIAL)
    vec3 metalScreenGeometryNormal = normal;
#endif

#if defined( USE_NORMALMAP_TANGENTSPACE )
    vec3 mapN = texture2D( normalMap, (dmNormalMapUvTransform * vec3(uvWithParallax, 1)).xy ).xyz * 2.0 - 1.0;
    mapN.xy *= normalScale;

    float mapNxyLength = length(mapN.xy);
    if (mapNxyLength > 0.01) {
      // Note that the dmNormalMapUvTransform values are transposed here.
      mapN.xy = mapNxyLength * normalize(mat2(dmNormalMapUvTransform[0][0], dmNormalMapUvTransform[1][0], dmNormalMapUvTransform[0][1], dmNormalMapUvTransform[1][1]) * mapN.xy);
    }
    mapN.z += 1.0 - min(abs(normalScale.x), abs(normalScale.y));

    mapN = normalize(mapN);

${applyHeightNormals}

    #ifdef APPLY_MAPN
        mapN = normalize(vec3(mapN.xy + dmDetailNormal.xy, mapN.z * dmDetailNormal.z));
        normal = normalize(dmTbn * mapN);
    #else
        normal = normalize(dmTbn * dmDetailNormal);
    #endif

#elif defined(SUPPORT_HEIGHT_LAYERS)
    vec3 mapN = vec3(0.0, 0.0, 1.0);
${applyHeightNormals}
    #ifdef APPLY_MAPN
        mapN = normalize(vec3(mapN.xy + dmDetailNormal.xy, mapN.z * dmDetailNormal.z));
        normal = normalize(dmTbn * mapN);
    #else
        normal = normalize(dmTbn * dmDetailNormal);
    #endif
#else
    normal = normalize(dmTbn * dmDetailNormal);
#endif

#if defined(METAL_SCREEN_MATERIAL)
    // Simulate a fine wire mesh: at grazing angles or from afar, the apparent normal
    // blends towards the view direction + geometry normal as the micro-geometry averages out.
    vec3 viewDir = normalize(vViewPosition);
    float viewDist = length(vViewPosition);

    // Grazing angle factor starts around 60 degrees (metalScreenNdotV ~0.5) and increases towards grazing
    float metalScreenNdotV = abs(dot(metalScreenGeometryNormal, viewDir));
    float msAngleFactor = smoothstep(0.5, 0.0, metalScreenNdotV) * 3.0;

    // Distance factor increases blending as we get further away
    float msDistanceFactor = smoothstep(20.0, 200.0, viewDist) * 1.7;

    // Combine both factors - either grazing angle OR distance triggers the effect
    float metalScreenBlendFactor = max(msAngleFactor, msDistanceFactor) * 0.7 * (1.0 - float(sampleIndex) * 0.01);
    // Blend normal towards the view direction
    normal = normalize(normal * (1.0 - metalScreenBlendFactor * 0.3) + (viewDir + metalScreenGeometryNormal * 0.5) * metalScreenBlendFactor);
#endif
`;
}
